/**
 * Settings ▸ Workspace — rename, slug, leave, danger zone (#501).
 *
 * Rename and slug changes go through PATCH /auth/v1/tenants/{id}
 * (owner/admin only — viewers and editors see the values read-only).
 * A slug change requires typing the new slug to confirm, because it
 * moves {slug}.systemdox.com and every URL under it. Leaving a
 * workspace calls the member-removal endpoint; the server rejects the
 * last owner with a 409 (shared-auth#121).
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, DoorOpen, Trash2 } from 'lucide-react';
import { LoadingState, Spinner } from '@systemdox/ui';
import { useAuthContext } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { errorStatus, problemDetail, tenantApi } from '@/services/tenantApi';
import { isValidSlug, tenantUrl, tenantHost, tenantHostSuffix } from '@/utils/subdomain';
import { cn } from '@/lib/utils';

const inputClass =
  'w-full max-w-md rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:border-purple-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed';

const sectionClass =
  'rounded-lg border border-slate-200 dark:border-slate-800 p-5';

function SectionHeading({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
      <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{description}</p>
    </div>
  );
}

export default function WorkspaceTab() {
  const { tenant, label, refreshTenants } = useTenant();
  const { user } = useAuthContext();

  const canManage = tenant?.role === 'owner' || tenant?.role === 'admin';
  const isOwner = tenant?.role === 'owner';

  // ── Rename ─────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    setName(tenant?.displayName ?? label ?? '');
  }, [tenant?.displayName, label]);

  const saveName = async () => {
    if (!tenant || !name.trim() || savingName) return;
    setSavingName(true);
    setNameError(null);
    setNameSaved(false);
    try {
      await tenantApi.updateTenant(tenant.tenantId, { displayName: name.trim() });
      await refreshTenants(); // the header badge picks the new name up without a reload
      setNameSaved(true);
    } catch (err) {
      setNameError(problemDetail(err, 'Could not rename the workspace.'));
    } finally {
      setSavingName(false);
    }
  };

  // ── Slug ───────────────────────────────────────────────────────────
  const [slugDraft, setSlugDraft] = useState('');
  const [slugConfirm, setSlugConfirm] = useState('');
  const [confirmingSlug, setConfirmingSlug] = useState(false);
  const [savingSlug, setSavingSlug] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [slugSaved, setSlugSaved] = useState(false);

  const normalizedSlug = slugDraft.trim().toLowerCase();
  const slugFormatOk = normalizedSlug === '' || isValidSlug(normalizedSlug);
  const slugUnchanged = normalizedSlug === (tenant?.slug ?? '');

  const saveSlug = async () => {
    if (!tenant || savingSlug || !normalizedSlug) return;
    if (slugConfirm.trim().toLowerCase() !== normalizedSlug) return;
    setSavingSlug(true);
    setSlugError(null);
    try {
      await tenantApi.updateTenant(tenant.tenantId, { slug: normalizedSlug });
      await refreshTenants();
      setConfirmingSlug(false);
      setSlugConfirm('');
      setSlugSaved(true);
    } catch (err) {
      setSlugError(
        errorStatus(err) === 409
          ? `The slug "${normalizedSlug}" is already taken.`
          : problemDetail(err, 'Could not change the slug.'),
      );
    } finally {
      setSavingSlug(false);
    }
  };

  // ── Leave ──────────────────────────────────────────────────────────
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const membershipUserId = useMemo(() => {
    const id = user?.userId ?? '';
    return id && !id.startsWith('usr_') ? `usr_${id}` : id;
  }, [user?.userId]);

  const leave = async () => {
    if (!tenant || !membershipUserId || leaving) return;
    setLeaving(true);
    setLeaveError(null);
    try {
      await tenantApi.leaveTenant(tenant.tenantId, membershipUserId);
      // Membership is gone; a full reload lands the user on their
      // remaining tenant via the usual token-refresh path.
      window.location.assign('/');
    } catch (err) {
      const status = errorStatus(err);
      setLeaving(false);
      if (status === 409) {
        setLeaveError(problemDetail(err, 'You are the last owner and cannot leave this workspace.'));
      } else if (status === 404) {
        setLeaveError('Leaving workspaces is not available yet — coming with member management.');
      } else {
        setLeaveError(problemDetail(err, 'Could not leave the workspace.'));
      }
    }
  };

  if (!tenant) {
    return (
      <LoadingState size="lg" label="Loading workspace…" />
    );
  }

  return (
    <div className="space-y-6">
      {!canManage && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          You have <span className="font-medium">{tenant.role ?? 'viewer'}</span> access — workspace
          settings are read-only. Ask an owner or admin to make changes.
        </p>
      )}

      {/* Rename */}
      <section className={sectionClass}>
        <SectionHeading
          title="Workspace name"
          description="Shown in the header badge, the workspace switcher, and page titles."
        />
        <div className="flex items-center gap-3">
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameSaved(false);
            }}
            disabled={!canManage || savingName}
            maxLength={100}
            placeholder="Workspace name"
            className={inputClass}
          />
          {canManage && (
            <button
              type="button"
              onClick={saveName}
              disabled={savingName || !name.trim() || name.trim() === (tenant.displayName ?? '')}
              className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingName ? <Spinner size="sm" tone="current" /> : 'Save'}
            </button>
          )}
          {nameSaved && (
            <span className="flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400">
              <Check className="w-4 h-4" /> Saved
            </span>
          )}
        </div>
        {nameError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{nameError}</p>}
      </section>

      {/* Slug */}
      <section className={sectionClass}>
        <SectionHeading
          title="Workspace URL"
          description={
            tenant.slug
              ? `Your workspace lives at ${tenantHost(tenant.slug)}.`
              : 'Claim a subdomain for your workspace.'
          }
        />
        <div className="flex items-center gap-3">
          <div className="flex max-w-md flex-1 items-center rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus-within:border-purple-500">
            <input
              value={slugDraft}
              onChange={(e) => {
                setSlugDraft(e.target.value);
                setSlugSaved(false);
                setSlugError(null);
              }}
              disabled={!canManage || savingSlug}
              placeholder={tenant.slug ?? 'your-workspace'}
              className="w-full bg-transparent px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none disabled:opacity-60"
            />
            <span className="pr-3 text-sm text-slate-400">{tenantHostSuffix()}</span>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={() => {
                setConfirmingSlug(true);
                setSlugConfirm('');
                setSlugError(null);
              }}
              disabled={savingSlug || !normalizedSlug || !slugFormatOk || slugUnchanged}
              className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Change…
            </button>
          )}
          {slugSaved && (
            <span className="flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400">
              <Check className="w-4 h-4" /> Saved
            </span>
          )}
        </div>
        {!slugFormatOk && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            Slugs are 3-63 characters: lowercase letters, numbers and hyphens, starting and ending
            with a letter or number.
          </p>
        )}
        {slugError && !confirmingSlug && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{slugError}</p>
        )}

        {confirmingSlug && (
          <div className="mt-4 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-4">
            <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0" />
              <div>
                <p className="font-medium">This changes your workspace URL.</p>
                <p className="mt-1">
                  {tenant.slug ? (
                    <>
                      <strong>{tenantHost(tenant.slug)}</strong> and every URL under it
                      will stop working; the workspace moves to{' '}
                      <strong>{tenantHost(normalizedSlug)}</strong>.
                    </>
                  ) : (
                    <>
                      The workspace will be served from{' '}
                      <strong>{tenantHost(normalizedSlug)}</strong>.
                    </>
                  )}{' '}
                  Type <strong>{normalizedSlug}</strong> to confirm.
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <input
                value={slugConfirm}
                onChange={(e) => setSlugConfirm(e.target.value)}
                placeholder={normalizedSlug}
                autoFocus
                className={cn(inputClass, 'max-w-xs')}
              />
              <button
                type="button"
                onClick={saveSlug}
                disabled={savingSlug || slugConfirm.trim().toLowerCase() !== normalizedSlug}
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingSlug ? <Spinner size="sm" tone="current" /> : 'Change slug'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmingSlug(false);
                  setSlugError(null);
                }}
                className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              >
                Cancel
              </button>
            </div>
            {slugError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{slugError}</p>}
          </div>
        )}
        {slugSaved && tenant.slug && (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Live at{' '}
            <a
              href={tenantUrl(tenant.slug)}
              className="text-purple-600 dark:text-purple-400 underline hover:no-underline"
            >
              {tenantHost(tenant.slug)}
            </a>
          </p>
        )}
      </section>

      {/* Leave */}
      {!isOwner && (
        <section className={sectionClass}>
          <SectionHeading
            title="Leave workspace"
            description="You lose access to this workspace's docs, specs and settings. An owner can invite you back."
          />
          {confirmingLeave ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={leave}
                disabled={leaving}
                className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {leaving ? <Spinner size="sm" tone="current" /> : <DoorOpen className="w-4 h-4" />}
                Yes, leave {label}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingLeave(false)}
                className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setConfirmingLeave(true);
                setLeaveError(null);
              }}
              className="flex items-center gap-2 rounded-md border border-red-300 dark:border-red-800 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              <DoorOpen className="w-4 h-4" />
              Leave workspace
            </button>
          )}
          {leaveError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{leaveError}</p>}
        </section>
      )}

      {/* Danger zone */}
      {isOwner && (
        <section className={cn(sectionClass, 'border-red-200 dark:border-red-900')}>
          <SectionHeading
            title="Danger zone"
            description="Deleting a workspace removes its docs, specs and members permanently."
          />
          <button
            type="button"
            disabled
            title="Workspace deletion is coming soon"
            className="flex items-center gap-2 rounded-md border border-red-300 dark:border-red-800 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 opacity-50 cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4" />
            Delete workspace
          </button>
          <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
            Not available yet — contact support if you need a workspace removed.
          </p>
        </section>
      )}
    </div>
  );
}
